/**
 * CE QU'UNE NON-CONFORMITÉ FAIT PROPOSER — et que rien n'écrit tout seul.
 * ──────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ C'EST UN MIROIR DÉCLARÉ de `backend/src/nc-declaration.ts`, et il est
 * SURVEILLÉ. Le front ne compile pas le moteur : les deux règles existent donc
 * en deux exemplaires, et `backend/tests/proposition-nc-parite.test.js` les
 * confronte sur tous les cas. Modifier l'un sans l'autre échoue là-bas.
 *
 * Pourquoi un miroir plutôt qu'un aller-retour au serveur : la proposition doit
 * s'afficher PENDANT la saisie, en face du bouton de gravité qu'on vient de
 * cliquer. Un appel réseau par clic afficherait la raison après coup, c'est-à-
 * dire trop tard pour qu'elle serve à décider.
 *
 * ── Ce que ces deux fonctions remplacent (arbitrage de la RQ, 11/10/2026) ───
 *
 * Deux automatismes vivaient dans le moteur et sont retirés :
 *
 *  · un PLANCHER relevait la gravité à « majeure » dès que des résultats
 *    patients étaient déclarés concernés. Relevé en base : 9 déclarations sur
 *    17 portaient une gravité que personne n'avait choisie — dont six « UAP non
 *    parvenu », où un prélèvement manquant ne dit rien d'un compte rendu faux.
 *  · une ACTION s'ouvrait d'office au plan. Relevé en base : 17 actions sur 23
 *    portaient le même intitulé, une par NC, aucune avec une échéance — donc
 *    aucune jamais signalée en retard, et aucune avancée.
 *
 * Les deux calculs subsistent à l'identique ; c'est leur DESTINATION qui change.
 * Ils vont à l'écran, et un geste humain les exécute.
 */

/** Du plus faible au plus fort — l'ordre donne son sens à la proposition. */
export const GRAVITES = ['mineure', 'majeure', 'critique'];

const txt = (v: unknown): string => String(v ?? '').trim();

/** La gravité telle qu'elle sera ENREGISTRÉE : celle qui est cochée, et rien
 *  d'autre. Elle ne regarde pas l'impact, et c'est tout le propos. */
export function graviteSaisie(gravite: unknown): string {
  return GRAVITES.includes(txt(gravite)) ? txt(gravite) : 'mineure';
}

/**
 * La gravité PROPOSÉE, `null` quand il n'y a rien à proposer.
 *
 * ⚠️ `inconnu` ne propose RIEN. « Je ne sais pas encore » est recevable et c'est
 * le défaut du formulaire : proposer sur un doute ferait de la proposition un
 * bruit de fond, et on confirmerait sans lire.
 *
 * ⚠️ Une gravité DÉJÀ au niveau ou au-dessus ne reçoit rien non plus : proposer
 * « majeure » à qui a coché « critique » donnerait l'impression qu'on cherche à
 * l'abaisser, c'est-à-dire l'inverse de ce que la règle dit.
 */
export function gravitePropose(
  gravite: unknown,
  impactResultats: unknown,
): { propose: string; raison: string } | null {
  if (txt(impactResultats) !== 'oui') return null;
  if (GRAVITES.indexOf(graviteSaisie(gravite)) >= GRAVITES.indexOf('majeure')) return null;
  return {
    propose: 'majeure',
    raison: 'Des résultats patients sont déclarés concernés : un compte rendu déjà rendu ne se '
      + 'rattrape pas au titre d’un écart mineur. À vous de trancher — rien n’est modifié sans vous.',
  };
}

/**
 * Faut-il PROPOSER d'ouvrir une action au plan ? — et pour quel motif.
 *
 * ⚠️ C'est la GRAVITÉ qui déclenche, pas l'impact. Une NC majeure ou critique
 * appelle une action quelle qu'en soit la cause ; l'impact sur les résultats
 * n'est qu'un des chemins qui y mènent — il sert à nommer le motif et à
 * pré-remplir l'intitulé, pas à décider.
 */
export function actionProposee(
  gravite: unknown,
  impactResultats: unknown,
): { proposer: boolean; titre: string; motif: string } {
  const g = graviteSaisie(gravite);
  if (GRAVITES.indexOf(g) < GRAVITES.indexOf('majeure')) return { proposer: false, titre: '', motif: '' };
  const resultats = txt(impactResultats) === 'oui';
  return {
    proposer: true,
    titre: resultats
      ? 'Statuer sur les résultats patients concernés (rappel, rectificatif, ou absence d’impact démontrée)'
      : '',
    motif: `Non-conformité « ${g} »`
      + (resultats ? ', et des résultats patients sont déclarés concernés' : '')
      + ' : le §8.7 attend une action au plan, ou la raison écrite de ne pas en ouvrir.',
  };
}
